import React, { useState, useEffect, useRef } from 'react';
import {
  Brain,
  MessageCircle,
  User,
  Bot,
  CheckCircle,
  Sparkles,
  Target,
  TrendingUp,
  Lightbulb,
  ChevronRight,
  ArrowRight,
  Play
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const AssessmentDemoActual = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [conversationStage, setConversationStage] = useState(0);
  const [showInsights, setShowInsights] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const [floatingKeywords, setFloatingKeywords] = useState([]);
  const [strengthMeters, setStrengthMeters] = useState({
    technical: 0,
    leadership: 0,
    community: 0,
    resilience: 0
  });
  const [typingMessage, setTypingMessage] = useState('');
  const [isInView, setIsInView] = useState(false);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [showGenerateButton, setShowGenerateButton] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const sectionRef = useRef(null);

  // Intersection observer for triggering animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView && !hasPlayedOnce) {
          setIsInView(true);
          setHasPlayedOnce(true);
          // Start conversation animation
          setTimeout(() => startConversation(), 500);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView, hasPlayedOnce]);

  const startConversation = () => {
    // Reset states for replay
    setConversationStage(0);
    setShowInsights(false);
    setIsProcessing(false);
    setProcessingStage(0);
    setFloatingKeywords([]);
    setStrengthMeters({ technical: 0, leadership: 0, community: 0, resilience: 0 });
    setAnimationsComplete(false);
    setShowGenerateButton(false);
    setButtonClicked(false);

    // Progressive conversation display - faster timings for quicker exchange
    const timings = [800, 2500, 4000, 5500];
    timings.forEach((timing, index) => {
      setTimeout(() => {
        setConversationStage(index + 1);
        if (index === timings.length - 1) {
          // Show generate button after conversation completes - longer pause to read
          setTimeout(() => {
            setShowGenerateButton(true);
            // Auto-click the button after a longer delay for reading
            setTimeout(() => {
              setButtonClicked(true);
              setShowGenerateButton(false);
              setIsProcessing(true);
              startProcessingSequence();
            }, 2000);
          }, 2500);
        }
      }, timing);
    });
  };

  // Processing sequence with multiple stages
  const startProcessingSequence = () => {
    // Stage 1: Initial processing
    setProcessingStage(1);

    // Stage 2: Discovering patterns - slower for comprehension
    setTimeout(() => {
      setProcessingStage(2);
      // Add all keywords at once for smooth animation
      const keywords = ['resilient', 'innovative', 'community-focused', 'pragmatic', 'determined'];
      setFloatingKeywords(keywords);
    }, 2500);

    // Stage 3: Building strength meters - more time to observe
    setTimeout(() => {
      setProcessingStage(3);
      // Animate strength meters with slightly longer delays
      setTimeout(() => setStrengthMeters(prev => ({ ...prev, technical: 95 })), 300);
      setTimeout(() => setStrengthMeters(prev => ({ ...prev, leadership: 88 })), 600);
      setTimeout(() => setStrengthMeters(prev => ({ ...prev, community: 92 })), 900);
      setTimeout(() => setStrengthMeters(prev => ({ ...prev, resilience: 97 })), 1200);
    }, 5500);

    // Stage 4: Complete - Show insights with more time to absorb
    setTimeout(() => {
      setIsProcessing(false);
      setShowInsights(true);
      // Mark animations complete and call callback - longer time to read results
      setTimeout(() => {
        setAnimationsComplete(true);
        onAnimationComplete && onAnimationComplete();
      }, 3000);
    }, 8500);
  };

  // Conversation data from aspirational version
  const conversation = [
    {
      type: 'bot',
      message: "Tell me about something you know a lot about that most people don't."
    },
    {
      type: 'user',
      message: "I got obsessed with optimizing my school's WiFi dead zones. Started mapping signal strength during lunch and figured out how to boost coverage using old routers I found in storage.",
      
    },
    {
      type: 'bot',
      message: "That's systems thinking in action - you didn't just complain, you actually fixed it. Is this a pattern for you or more of a one-off?",
    },
    {
      type: 'user',
      message: "Yeah, I guess I do this a lot. Like when our robotics team's code kept failing, I spent a weekend debugging everyone else's work and reorganizing the whole thing. I just... see inefficient systems and can't leave them alone.",

    }
  ];

  // Assessment result data
  const assessmentResults = {
    strategicAdvantage: "You have a rare combination: the ability to spot systemic problems AND the drive to actually solve them.",

    patterns: [
      {
        title: "Systems Thinking + Action",
        description: "You bridge theory with real-world implementation"
      },
      {
        title: "Failure-Driven Learning",
        description: "You use setbacks as data for improvement"
      },
      {
        title: "Community-Centered Engineering",
        description: "You frame technical work by human impact"
      }
    ],

    uniqueStory: {
      coreTheme: "The Rational Innovator",
      themeDescription: "You don't just build things; you build solutions that work in messy reality.",
      essayAngle: "Focus on the moment you realized elegant code means nothing if it doesn't solve real problems."
    },

    nextStep: "Emphasize hands-on problem-solving in your MIT application"
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4 relative">

      {/* Section Header */}
      <div className="text-center mb-6 lg:mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 rounded-full">
            <Brain className="w-4 h-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">
              Strategic Assessment
            </span>
          </div>
        </div>
        <h2 className="text-2xl lg:text-4xl font-bold text-slate-900 mb-2 lg:mb-4">
          Discover Your Authentic Story
        </h2>
        <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
        The most commonly given college advice is 'be yourself' - but what if you don't know what makes you special? You'll discover it through real conversation.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Conversation Interface */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
          <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-900">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Strategic Discovery Conversation
            </h3>
          </div>

          <div className="p-4 lg:p-6 space-y-3 lg:space-y-4 h-[420px] lg:h-[580px] overflow-y-auto">
            {conversation.slice(0, conversationStage).map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.type === 'user' ? 'flex-row-reverse' : ''}
                  animate-fadeIn`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.type === 'user' ? 'bg-slate-50 border border-slate-400' : 'bg-slate-800 '}`}
                >
                  {msg.type === 'user' ? (
                    <User className="w-4 h-4 text-slate-900" />
                  ) : (
                    <Bot className="w-4 h-4 text-white" />
                  )}
                </div>

                <div className={`flex-1 ${msg.type === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block px-4 py-2 rounded-lg max-w-[80%]
                    ${msg.type === 'user'
                      ? 'border border-slate-300 text-slate-900 text-left'
                      : 'bg-slate-100 text-slate-900'}`}
                  >
                    {msg.highlight ? (
                      <span>
                        {msg.message.split(' ').map((word, i) => {
                          const isHighlight = msg.highlight.some(h =>
                            word.toLowerCase().includes(h.toLowerCase().replace(/[^\w\s]/g, '')));
                          return (
                            <span key={i}>
                              {isHighlight ? (
                                <span className="bg-azure-500/30 px-1 rounded">
                                  {word}
                                </span>
                              ) : word}
                              {' '}
                            </span>
                          );
                        })}
                      </span>
                    ) : msg.message}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {conversationStage > 0 && conversationStage < 4 && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-slate-50" />
                </div>
                <div className="px-4 py-2 bg-slate-100 rounded-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-100" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-200" />
                  </div>
                </div>
              </div>
            )}

            {/* Generate Strategic Assessment Button */}
            {showGenerateButton && (
              <div className="mt-6 text-center animate-fadeInScale">
                <button className={`px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 ${
                  buttonClicked ? 'scale-95 opacity-50' : 'hover:shadow-xl'
                }`}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate Strategic Assessment
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Assessment Results Card */}
        <div className={`bg-white rounded-xl border-2 border-slate-900 shadow-2xl overflow-hidden relative transition-all duration-700 ${
          (isProcessing || showInsights) ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          {/* Processing Overlay - Shows in the right column */}
          {isProcessing && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="text-center max-w-md px-6">
                {processingStage === 1 && (
                  <>
                    <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Brain className="w-8 h-8 text-violet-600 animate-pulse" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      Analyzing your responses...
                    </h3>
                    <p className="text-sm text-slate-600">
                      Looking for patterns in how you approach problems
                    </p>
                  </>
                )}

                {processingStage === 2 && (
                  <>
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-indigo-600 animate-spin" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      Discovering something remarkable about you...
                    </h3>
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {floatingKeywords.map((keyword, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium"
                          style={{
                            animation: 'fadeInUp 0.6s ease-out forwards',
                            animationDelay: `${i * 100}ms`,
                            opacity: 0
                          }}
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {processingStage === 3 && (
                  <>
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Building your strength profile...
                    </h3>
                    <div className="space-y-2 text-left">
                      {Object.entries(strengthMeters).map(([key, value]) => (
                        <div key={key}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium capitalize text-slate-700">{key}</span>
                            <span className="text-sm text-slate-600">{value}%</span>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500"
                              style={{ width: `${value}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-stone-50 to-white px-6 py-5 text-center border-b-2 border-slate-900">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-magenta-400 to-orange-500 text-white rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              Assessment Complete
            </div>
            <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-1">Here's what makes you unique</h3>
            <p className="text-sm text-slate-600">and exactly how to leverage it</p>
          </div>

          {/* Content */}
          <div className="p-4 lg:p-6 space-y-3 lg:space-y-5 max-h-[350px] lg:max-h-[500px] overflow-y-auto">
            {/* Strategic Advantage - Highlight Box */}
            <div className={`bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center transition-all duration-500 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="text-xs font-bold text-yellow-700 uppercase tracking-wider mb-2">
                Your Strategic Advantage
              </div>
              <p className="text-yellow-900 font-medium leading-relaxed">
                {assessmentResults.strategicAdvantage}
              </p>
            </div>

            {/* Patterns Section */}
            <div className={`transition-all duration-500 delay-100 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-lg">
                  🔍
                </div>
                <h4 className="font-bold text-teal-900 text-lg">What I noticed about you</h4>
              </div>

              <div className="space-y-3">
                {assessmentResults.patterns.map((pattern, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200"
                    style={{
                      animationDelay: `${200 + index * 100}ms`,
                      animation: showInsights ? 'fadeInUp 0.5s ease-out forwards' : 'none'
                    }}
                  >
                    <div className="w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold">✓</span>
                    </div>
                    <div>
                      <div className="font-semibold text-teal-900 text-sm">{pattern.title}</div>
                      <div className="text-xs text-teal-700 mt-0.5">{pattern.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Story Section */}
            <div className={`bg-indigo-50 border-2 border-indigo-600 rounded-xl p-5 transition-all duration-500 delay-300 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-lg">
                  💡
                </div>
                <h4 className="font-bold text-indigo-900 text-lg">Your Unique Story</h4>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                  <div className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1">
                    Core Theme
                  </div>
                  <div className="font-bold text-indigo-900 mb-2">
                    "{assessmentResults.uniqueStory.coreTheme}"
                  </div>
                  <div className="text-sm text-slate-700 italic">
                    {assessmentResults.uniqueStory.themeDescription}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                  <div className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1">
                    Essay Angle
                  </div>
                  <div className="text-sm text-slate-700 italic">
                    {assessmentResults.uniqueStory.essayAngle}
                  </div>
                </div>
              </div>
            </div>

            {/* Next Step */}
            <div className={`bg-white border-2 border-slate-900 rounded-xl p-5 transition-all duration-500 delay-500 ${showInsights ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-azure-500 text-white rounded-full flex items-center justify-center text-lg">
                  🎯
                </div>
                <h4 className="font-bold text-slate-900 text-lg">Your next step</h4>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                  1
                </div>
                <span className="font-medium text-slate-900">
                  {assessmentResults.nextStep}
                </span>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Section Controls */}
      <SectionControls
        onReplay={startConversation}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AssessmentDemoActual;